import {
  createBrowserRouter,
  redirect,
  Navigate,
  useParams,
  ShouldRevalidateFunctionArgs,
} from 'react-router-dom'
import { toast } from 'react-toastify'
import * as Sentry from '@sentry/react'

import PublicLayout from './features/root/PublicLayout'
import ProtectedLayout from './features/root/ProtectedLayout'
import RootBoundary from './features/root/RootBoundary'
import Login from './features/login/Login'
import { authenticate, wordpressAuthenticate, logout } from './features/login/Login.action'
import Profile from './features/profile/Profile'
import { profileLoader } from './features/profile/Profile.loader'
import { submitProfile } from './features/profile/Profile.action'
import Dashboard from './features/dashboard/Dashboard'
import { dashboardLoader } from './features/dashboard/Dashboard.loader'
import ViewCertificates from './features/certificates/ViewCertificates'
import { certificatesLoader } from './features/certificates/ViewCertificates.loader'
import Agenda from './features/registrations/Agenda'
import EmptyAgenda from './features/registrations/components/EmptyAgenda'
import AgendaDetails from './features/registrations/AgendaDetails'
import { agendaDetailsLoader, agendaLoader } from './features/registrations/Agenda.loader'
import AbstractsLayout from './features/abstracts/Abstracts.Layout'
import EmptySelection from './features/abstracts/EmptySelection'
import {
  abstractSubmitListEventsLoader,
  abstractReviewListEventsLoader,
  abstractLoader,
  abstractDetailsForReviewLoader,
  listAbstractsLoader,
  listAbstractReviewsLoader,
} from './features/abstracts/Abstracts.loader'
import { rateAbstract, submitAbstract } from './features/abstracts/Abstracts.action'
import AbstractsSubmitListLayout from './features/abstracts/AbstractsSubmitList.Layout'
import AbstractsEvaluationListLayout from './features/abstracts/AbstractsEvaluationList.Layout'
import AbstractsSubmitList from './features/abstracts/tabs/submit/AbstractsSubmitList'
import AbstractsEvaluationList from './features/abstracts/tabs/evaluation/AbstractsEvaluationList'
import AbstractEdit from './features/abstracts/tabs/submit/AbstractEdit'
import AbstractReview from './features/abstracts/tabs/evaluation/AbstractReview'
import AdminLayout from './features/admin/Admin.Layout'
import Admin from './features/admin/Admin'
import AdminSubEvents from './features/admin/details/AdminSubEvents'
import AdminHotels from './features/admin/details/AdminHotels'
import AdminMeals from './features/admin/details/AdminMeals'
import AdminContributors from './features/admin/details/AdminContributors'
import AdminParticipants from './features/admin/details/AdminParticipants'
import AdminOptionParticipants from './features/admin/details/AdminOptionParticipants'
import AdminParticipantDetails from './features/admin/details/AdminParticipantDetails'
import AdminOptions from './features/admin/details/AdminOptions'
import AdminAbstracts from './features/admin/details/AdminAbstracts'
import AdminSessionAbstracts from './features/admin/details/AdminSessionAbstracts'
import AdminSponsorship from './features/admin/details/AdminSponsorship'
import EditParticipantOptions from './features/admin/details/components/EditParticipantOptions'
import EditParticipant from './features/admin/details/components/EditParticipant'
import {
  adminEventsLoader,
  adminOptionsLoader,
  adminOptionsParticipantsLoader,
  adminEventDetailsLoader,
  adminParticipantsLoader,
  adminParticipantDetailsLoader,
  adminHotelOverviewLoader,
  adminMealOverviewLoader,
  adminContributorOverviewLoader,
  adminAbstractsLoader,
  adminSessionAbstractsLoader,
  adminSponsorshipLoader,
  adminSponsorshipByIdLoader,
} from './features/admin/Admin.loader'
import EditSponsorship from './features/admin/details/components/EditSponsorship'
import {
  putOptionParticipation,
  putParticipant,
  putSponsorship,
} from './features/admin/Admin.action'
import ModifySubEvent from './features/admin/create-event/step-2/components/ModifySubEvent'
import ModifySession from './features/admin/create-event/step-2/components/ModifySession'
import ViewEvents from './features/events/view/ViewEvents'
import { modifySubEvent, modifySession } from './features/events/view/View.action'
import ViewSubEvent from './features/events/view/ViewSubEvent'
import ViewSession from './features/events/view/ViewSession'
import { subEventLoader, sessionLoader } from './features/events/view/View.loader'
import RegisterEvents from './features/events/register/RegisterEvents'
import { registerEvent } from './features/events/register/RegisterEvents.action'
import { eventRegistrationDetailsLoader } from './features/events/register/RegisterEvents.loader'
import CheckinEvent from './features/events/checkin/CheckinEvent'
import { eventCheckin, subEventCheckin } from './features/events/checkin/CheckinEvent.loader'
import SubmitSponsorship from './features/events/sponsorship/SubmitSponsorship'
import { mySponsorshipLoader } from './features/events/sponsorship/SubmitSponsorship.loader'
import { submitSponsorship } from './features/events/sponsorship/SubmitSponsorship.action'
import CreateEventsLayout from './features/admin/create-event/CreateEvents.Layout'
import EventStep1 from './features/admin/create-event/step-1/EventStep1'
import SubEventsStep2 from './features/admin/create-event/step-2/SubEventsStep2'
import LogisticStep3 from './features/admin/create-event/step-3/LogisticStep3'
import ConditionsStep4 from './features/admin/create-event/step-4/ConditionsStep4'
import ModifyOption from './features/admin/create-event/step-4/components/ModifyOption'
import ValidationStep5 from './features/admin/create-event/step-5/ValidationStep5'
import { submitEvent } from './features/admin/create-event/CreateEvents.action'
import { eventLoader, adminEventLoader } from './features/events/Events.loader'
import { userApi } from './services/user'
import { optionsApi } from './services/options'
import { sponsorshipApi } from './services/sponsorship'
import type { AppStore } from './store'
import { getCurrentUserToken, isCurrentUserStaff } from './store.slice'
import i18n from './i18n'
import { replace } from './utils'

const rootLoader = (store: AppStore) => () => {
  const token = getCurrentUserToken(store.getState())
  if (token) {
    return replace(`/dashboard`)
  }
  return replace(`/login`)
}

const publicLoader = (store: AppStore) => async () => {
  await store.dispatch(optionsApi.endpoints.config.initiate())
  await store.dispatch(optionsApi.endpoints.listTags.initiate())
  await store.dispatch(optionsApi.endpoints.listLocations.initiate())

  return null
}

const protectedLoader =
  (store: AppStore) =>
  async ({ request }: { request: Request }) => {
    const token = getCurrentUserToken(store.getState())
    if (!token) {
      const params = new URLSearchParams()
      params.set('from', new URL(request.url).pathname)
      return replace(`/login?${params.toString()}`)
    }

    await store.dispatch(userApi.endpoints.currentUser.initiate())

    // Loads Opts at first run
    await store.dispatch(optionsApi.endpoints.config.initiate())
    await store.dispatch(optionsApi.endpoints.listTags.initiate())
    await store.dispatch(optionsApi.endpoints.listLocations.initiate())
    await store.dispatch(optionsApi.endpoints.listJobTitles.initiate())
    await store.dispatch(optionsApi.endpoints.listDomains.initiate())
    await store.dispatch(optionsApi.endpoints.listThemes.initiate())
    await store.dispatch(optionsApi.endpoints.listKeywords.initiate())
    await store.dispatch(optionsApi.endpoints.listLaboratories.initiate())
    await store.dispatch(sponsorshipApi.endpoints.listSponsorshipsType.initiate())

    return null
  }

const adminLoader =
  (store: AppStore) =>
  async ({ request }: { request: Request }) => {
    const token = getCurrentUserToken(store.getState())
    if (!token) {
      const params = new URLSearchParams()
      params.set('from', new URL(request.url).pathname)
      return replace(`/login?${params.toString()}`)
    }

    await store.dispatch(userApi.endpoints.currentUser.initiate())

    const isStaff = isCurrentUserStaff(store.getState())

    if (!isStaff) {
      return replace(`/dashboard`)
    }

    return null
  }

const alreadyLoggedIn = (store: AppStore) => () => {
  const token = getCurrentUserToken(store.getState())
  if (token) {
    return replace(`/dashboard`)
  }
  return null
}

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV6(createBrowserRouter)

// TODO not pass store to router when a better solution is found
// https://github.com/reduxjs/redux-toolkit/discussions/2751
export const router = (store: AppStore) =>
  sentryCreateBrowserRouter(
    [
      {
        id: 'root',
        path: '/',
        ErrorBoundary: RootBoundary,
        children: [
          {
            index: true,
            loader: rootLoader(store),
          },
          {
            path: 'login',
            Component: Login,
            loader: alreadyLoggedIn(store),
            action: authenticate(store),
          },
          {
            path: 'wordpress_login_callback',
            loader: wordpressAuthenticate(store),
          },
          {
            path: 'logout',
            loader: logout(store),
          },
          {
            path: 'public',
            Component: PublicLayout,
            loader: publicLoader(store),
            children: [
              {
                index: true,
                Component: () => <Navigate to="/public/dashboard" replace />,
              },
              {
                path: 'dashboard',
                Component: Dashboard,
                loader: dashboardLoader(store),
              },
              {
                path: 'events/:eventId',
                id: 'public-view-event',
                Component: ViewEvents,
                loader: eventLoader(store),
                shouldRevalidate: (params: ShouldRevalidateFunctionArgs) => {
                  const { currentParams, nextParams, defaultShouldRevalidate, formAction } = params
                  // Prevent revalidation if the eventId is the same
                  if (currentParams.eventId === nextParams.eventId) {
                    // Force refect if formAction is present
                    if (formAction) return true
                    return false
                  }
                  return defaultShouldRevalidate
                },
                children: [
                  {
                    path: 'subEvent/:subEventId',
                    Component: () => ViewSubEvent({ loaderId: 'public-view-event' }),
                    loader: subEventLoader(store),
                  },
                  {
                    path: 'session/:sessionId',
                    Component: () => ViewSession({ loaderId: 'public-view-event' }),
                    loader: sessionLoader(store),
                  },
                ],
              },
            ],
          },
          {
            loader: protectedLoader(store),
            Component: ProtectedLayout,
            children: [
              {
                path: 'profile',
                Component: Profile,
                loader: profileLoader(store),
                action: submitProfile(store),
              },
              {
                path: 'dashboard',
                Component: Dashboard,
                loader: dashboardLoader(store),
              },
              {
                path: 'inscriptions',
                id: 'inscriptions',
                Component: Agenda,
                loader: agendaLoader(store),
                children: [
                  {
                    index: true,
                    Component: EmptyAgenda,
                  },
                  {
                    path: ':eventId',
                    id: 'agenda-view-details',
                    loader: agendaDetailsLoader(store),
                    Component: AgendaDetails,
                    children: [
                      {
                        path: 'subEvent/:subEventId',
                        Component: () => ViewSubEvent({ loaderId: 'agenda-view-details' }),
                        loader: subEventLoader(store),
                      },
                      {
                        path: 'session/:sessionId',
                        Component: () => ViewSession({ loaderId: 'agenda-view-details' }),
                        loader: sessionLoader(store),
                      },
                    ],
                  },
                ],
              },
              {
                path: 'certificates',
                Component: ViewCertificates,
                loader: certificatesLoader(store),
              },
              {
                path: 'abstracts',
                id: 'abstracts',
                Component: AbstractsLayout,
                children: [
                  {
                    index: true,
                    Component: () => <Navigate to="/abstracts/list" replace />,
                  },
                  {
                    path: 'list',
                    id: 'submit-abstracts-list',
                    Component: AbstractsSubmitListLayout,
                    loader: abstractSubmitListEventsLoader(store),
                    children: [
                      {
                        index: true,
                        Component: EmptySelection,
                      },
                      {
                        path: ':eventId',
                        Component: AbstractsSubmitList,
                        loader: listAbstractsLoader(store),
                      },
                      {
                        path: ':eventId/abstract/:abstractId/submit',
                        Component: AbstractEdit,
                        loader: abstractLoader(store),
                        action: submitAbstract(store),
                      },
                    ],
                  },
                  {
                    path: 'evaluation',
                    id: 'review-abstracts-list',
                    Component: AbstractsEvaluationListLayout,
                    loader: abstractReviewListEventsLoader(store),
                    children: [
                      {
                        index: true,
                        Component: EmptySelection,
                      },
                      {
                        path: ':eventId',
                        Component: AbstractsEvaluationList,
                        loader: listAbstractReviewsLoader(store),
                      },
                      {
                        path: ':eventId/rate/:abstractId',
                        Component: AbstractReview,
                        loader: abstractDetailsForReviewLoader(store),
                        action: rateAbstract(store),
                      },
                    ],
                  },
                ],
              },
              {
                path: 'events/:eventId',
                id: 'view-event',
                Component: ViewEvents,
                loader: eventLoader(store),
                shouldRevalidate: (params: ShouldRevalidateFunctionArgs) => {
                  const { currentParams, nextParams, defaultShouldRevalidate, formAction } = params
                  // Prevent revalidation if the eventId is the same
                  if (currentParams.eventId === nextParams.eventId) {
                    // Force refect if formAction is present
                    if (formAction) return true
                    return false
                  }
                  return defaultShouldRevalidate
                },
                children: [
                  {
                    path: 'subEvent/:subEventId',
                    Component: () => ViewSubEvent({ loaderId: 'view-event' }),
                    loader: subEventLoader(store),
                    action: modifySubEvent(store),
                  },
                  {
                    path: 'session/:sessionId',
                    Component: () => ViewSession({ loaderId: 'view-event' }),
                    loader: sessionLoader(store),
                    action: modifySession(store),
                  },
                  {
                    path: 'register',
                    Component: RegisterEvents,
                    loader: eventRegistrationDetailsLoader(store),
                    action: registerEvent(store),
                  },
                  {
                    path: 'checkin',
                    Component: CheckinEvent,
                    loader: eventCheckin(store),
                  },
                  {
                    path: 'subEvent/:subEventId/checkin',
                    Component: CheckinEvent,
                    loader: subEventCheckin(store),
                  },
                  {
                    path: 'sponsorship',
                    Component: SubmitSponsorship,
                    loader: mySponsorshipLoader(store),
                    action: submitSponsorship(store),
                  },
                ],
              },
              {
                path: '/checkout_success/',
                loader: () => {
                  const { t } = i18n
                  toast.success(t('Inscription réussie'))
                  return redirect('/dashboard')
                },
              },
              {
                path: '/checkout_failure/',
                loader: () => {
                  const { t } = i18n
                  toast.error(t("Erreur lors de l'inscription"))
                  return redirect('/dashboard')
                },
              },
              {
                path: 'admin',
                loader: adminLoader(store),
                Component: AdminLayout,
                children: [
                  {
                    index: true,
                    Component: Admin,
                    loader: adminEventsLoader(store),
                  },
                  {
                    path: 'details',
                    Component: () => <Navigate to="/admin" replace />,
                  },
                  {
                    path: 'details/:eventId',
                    id: 'adminEventDetails',
                    loader: adminEventDetailsLoader(store),
                    children: [
                      {
                        index: true,
                        Component: () => <Navigate to="subevents" replace />,
                      },
                      {
                        path: 'subevents',
                        Component: AdminSubEvents,
                      },
                      {
                        path: 'options',
                        Component: AdminOptions,
                        loader: adminOptionsLoader(store),
                      },
                      {
                        path: 'options/:optionId',
                        id: 'optionsDetails',
                        Component: AdminOptionParticipants,
                        loader: adminOptionsParticipantsLoader(store),
                        children: [
                          {
                            path: 'details/:currentParticipation',
                            Component: EditParticipantOptions,
                            action: putOptionParticipation(store),
                          },
                        ],
                      },
                      {
                        path: 'abstracts',
                        id: 'adminAbstracts',
                        Component: AdminAbstracts,
                        loader: adminAbstractsLoader(store),
                      },
                      {
                        path: 'sponsorship',
                        id: 'adminSponsorship',
                        Component: AdminSponsorship,
                        loader: adminSponsorshipLoader(store),
                        children: [
                          {
                            path: ':sponsorshipId',
                            Component: EditSponsorship,
                            loader: adminSponsorshipByIdLoader(store),
                            action: putSponsorship(store),
                          },
                        ],
                      },
                      {
                        path: 'abstracts/sessions',
                        Component: AdminSessionAbstracts,
                        loader: adminSessionAbstractsLoader(store),
                      },
                      {
                        path: 'hotels',
                        Component: AdminHotels,
                        loader: adminHotelOverviewLoader(store),
                      },
                      {
                        path: 'meals',
                        Component: AdminMeals,
                        loader: adminMealOverviewLoader(store),
                      },
                      {
                        path: 'contributors',
                        Component: AdminContributors,
                        loader: adminContributorOverviewLoader(store),
                      },
                      {
                        path: 'participants',
                        id: 'participants',
                        Component: AdminParticipants,
                        loader: adminParticipantsLoader(store),
                        children: [
                          {
                            path: 'details/:currentParticipant',
                            Component: EditParticipant,
                            action: putParticipant(store),
                          },
                        ],
                      },
                      {
                        id: 'participantsDetails',
                        path: 'participants/:participantId',
                        Component: AdminParticipantDetails,
                        loader: adminParticipantDetailsLoader(store),
                        children: [
                          {
                            path: 'option/:currentParticipation',
                            Component: EditParticipantOptions,
                            action: putOptionParticipation(store),
                          },
                        ],
                      },
                    ],
                  },
                  {
                    path: 'events/:eventId',
                    id: 'events',
                    Component: CreateEventsLayout,
                    loader: adminEventLoader(store),
                    shouldRevalidate: (params: ShouldRevalidateFunctionArgs) => {
                      const { currentParams, nextParams, defaultShouldRevalidate } = params
                      // Prevent revalidation if the eventId is the same
                      if (currentParams.eventId === nextParams.eventId) {
                        return false
                      }
                      return defaultShouldRevalidate
                    },
                    children: [
                      {
                        index: true,
                        Component: () => {
                          const { eventId } = useParams()
                          return <Navigate to={`/admin/events/${eventId}/event`} replace />
                        },
                      },
                      {
                        path: 'event',
                        Component: EventStep1,
                        action: submitEvent(store),
                      },
                      {
                        path: 'sessions',
                        Component: SubEventsStep2,
                        action: submitEvent(store),
                        children: [
                          {
                            path: ':sessionId',
                            Component: ModifySession,
                          },
                          {
                            path: 'subEvent/:subEventId',
                            Component: ModifySubEvent,
                          },
                        ],
                      },
                      {
                        path: 'logistic',
                        Component: LogisticStep3,
                        action: submitEvent(store),
                      },
                      {
                        path: 'conditions',
                        Component: ConditionsStep4,
                        action: submitEvent(store),
                        children: [
                          {
                            path: ':optionId',
                            Component: ModifyOption,
                          },
                        ],
                      },
                      {
                        path: 'validation',
                        Component: ValidationStep5,
                        action: submitEvent(store),
                        children: [
                          {
                            path: 'subEvent/:subEventId',
                            Component: () => {
                              const { eventId, subEventId } = useParams()
                              return (
                                <Navigate
                                  to={`/admin/events/${eventId}/sessions/subEvent/${subEventId}`}
                                  replace
                                />
                              )
                            },
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
    {
      future: {
        v7_skipActionErrorRevalidation: true,
      },
    }
  )
